import type {
  ArenaLeaderboardEntry,
  CardDuelLeaderboardEntry,
  LegacyCardDuelLeaderboardEntry,
  DuelLeaderboardEntry,
  GoldLeaderboardEntry,
  LeaderboardBoard,
  LeaderboardEntry,
  LeaderboardPage,
  StormMarksLeaderboardEntry,
  SphereLeaderboardEntry,
} from '../leaderboard';

/** Any board's row shape - the union every pager/window consumer narrows from. */
export type AnyLeaderboardEntry =
  | LeaderboardEntry
  | GoldLeaderboardEntry
  | DuelLeaderboardEntry
  | StormMarksLeaderboardEntry
  | ArenaLeaderboardEntry
  | CardDuelLeaderboardEntry
  | LegacyCardDuelLeaderboardEntry
  | SphereLeaderboardEntry;

/**
 * The shape of the one paged leaderboard fetch every consumer shares (the splash
 * panel, the in-world board window, the Arena Master gump). Kept as a type so
 * feature installers can take it as a seam and tests can hand in a fake.
 */
export type LeaderboardFetcher = (
  offset: number,
  me?: string,
  board?: LeaderboardBoard,
  season?: 'last' | 'legacy' | number,
  challenge?: string,
) => Promise<LeaderboardPage<AnyLeaderboardEntry> | null>;

/**
 * Build the one paged leaderboard fetcher against a server's HTTP origin (the
 * `GET /leaderboard` route - Lightweight Accounts Slice D, ADR-0043; paged since
 * Issue #195). `offset` picks the page (the server slices LEADERBOARD_PAGE_SIZE
 * rows); a non-empty `me` asks the server for that viewer's own current rank in the
 * same round trip, which the board window's "Jump to your position" resolves a page
 * from; `board` selects the ranking (Rat Swarm Slice F): `'gold'` adds
 * `?board=gold` (the durable-gold board, ADR-0115), `'duels'` adds `?board=duels`
 * (the win/loss board, ADR-0147), `'storm'` adds `?board=storm` (the Storm Marks
 * board, ADR-0149/0153), `'arena'` adds `?board=arena` (the Arena board, ADR-0172),
 * `'cards'` adds `?board=cards` (live Elo, with `season=legacy` for testing W-L,
 * ADR-0353), anything
 * else is the classic online-time default - the
 * server treats an absent/unknown value identically, so omitting the param keeps
 * old-shape requests byte-identical. Returns the page, or `null` on any failure
 * (server down, network error, malformed body) - both the splash and the board
 * window treat `null` as "standings unavailable", so a leaderboard outage never
 * blocks entering the bank.
 */
export function makeLeaderboardFetcher(leaderboardUrl: string): LeaderboardFetcher {
  return async (offset, me, board, season, challenge) => {
    try {
      const url = new URL(leaderboardUrl);
      if (offset > 0) url.searchParams.set('offset', String(offset));
      if (me) url.searchParams.set('me', me);
      // Any non-time board rides the `board` param verbatim (`gold`, ADR-0115;
      // `duels`, ADR-0147; `storm`, ADR-0149/0153); the time default omits it, keeping
      // old requests byte-identical. Gated on `!== 'time'` rather than an allow-list so a
      // future board can never silently fall back to the time ranking (the Slice F bug).
      if (board && board !== 'time') url.searchParams.set('board', board);
      // An archived Arena season (arena-seasons): `last` or an exact number. Only
      // meaningful with `board=arena`; omitted on a live read so those requests
      // stay byte-identical.
      if (season !== undefined) url.searchParams.set('season', String(season));
      if (challenge !== undefined) url.searchParams.set('challenge', challenge);
      const res = await fetch(url);
      if (!res.ok) return null;
      const body = (await res.json()) as Partial<LeaderboardPage>;
      if (!Array.isArray(body.entries) || typeof body.total !== 'number') return null;
      return {
        entries: body.entries,
        total: body.total,
        offset: typeof body.offset === 'number' ? body.offset : offset,
        rank: typeof body.rank === 'number' ? body.rank : null,
        // The season stamp (arena-seasons): a number on an arena read, null when the
        // asked-for archive doesn't exist, absent on the other boards - forwarded
        // verbatim so the window can label the board / render the empty state.
        ...(body.season !== undefined ? { season: body.season } : {}),
        ...(body.cardView === 'elo' || body.cardView === 'legacy'
          ? { cardView: body.cardView }
          : {}),
      };
    } catch {
      return null;
    }
  };
}
